import { RecentlyViewed } from '@theme/recently-viewed-products';

const SECTION_FILE = 'recently-viewed-products';
/** New visitors with a short history still get a full first row, padded with bestsellers. */
const MIN_CARDS = 4;
const routesRoot = () => window.Shopify?.routes?.root ?? '/';

/**
 * Fetches the card for one product, rendered by Shopify from the recently viewed section
 * in that product's context, so it matches the other product cards exactly.
 * @param {string} handle
 * @returns {Promise<Element | null>}
 */
async function fetchCard(handle) {
  try {
    const response = await fetch(`${routesRoot()}products/${encodeURIComponent(handle)}?section_id=${SECTION_FILE}`);
    if (!response.ok) return null;
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return html.querySelector('[data-recently-viewed-card]');
  } catch {
    return null;
  }
}

/**
 * @param {string[]} handles
 * @returns {Promise<Element[]>}
 */
async function fetchCards(handles) {
  const cards = await Promise.all(handles.map(fetchCard));
  return cards.filter((card) => card !== null);
}

class RecentlyViewedProductsList extends HTMLElement {
  #dragReady = false;

  async connectedCallback() {
    const section = this.closest('.recently-viewed-section');
    const max = parseInt(this.dataset.max ?? '', 10) || 4;
    const minimum = Math.min(max, MIN_CARDS);
    const currentHandle = this.dataset.currentHandle;

    const handles = RecentlyViewed.getProductHandles()
      .filter((handle) => handle && handle !== currentHandle)
      .slice(0, max);

    let cards = await fetchCards(handles);

    // Show at least `minimum` products: pad out with bestsellers when real
    // browsing history is too short (new visitor, only viewed 1-2 items).
    if (cards.length < minimum) {
      const seenHandles = new Set([currentHandle, ...handles]);
      const needed = minimum - cards.length;

      const fallbackList = await fetch(
        `${routesRoot()}collections/homepage-best-sellers/products.json?limit=${needed + seenHandles.size}`
      )
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);

      if (fallbackList && Array.isArray(fallbackList.products)) {
        const fillerHandles = fallbackList.products
          .map((/** @type {{ handle: string }} */ product) => product.handle)
          .filter((handle) => handle && !seenHandles.has(handle))
          .slice(0, needed);

        cards = cards.concat(await fetchCards(fillerHandles));
      }
    }

    if (cards.length === 0) return;

    this.replaceChildren(...cards.slice(0, max));

    if (section instanceof HTMLElement) section.hidden = false;
    this.#enableMouseDrag();
    this.#enableArrows();
  }

  /**
   * Wires the desktop arrows: each click moves one card; they show only when the row scrolls,
   * and the arrow at the start or end of the row is disabled (and hidden by CSS).
   */
  #enableArrows() {
    const arrows = this.parentElement?.querySelector('.recently-viewed-arrows');
    if (!(arrows instanceof HTMLElement) || arrows.dataset.ready) return;
    arrows.dataset.ready = 'true';

    const previous = arrows.querySelector('[data-recently-viewed-step="-1"]');
    const next = arrows.querySelector('[data-recently-viewed-step="1"]');

    const update = () => {
      const maxScroll = this.scrollWidth - this.clientWidth;
      arrows.hidden = maxScroll <= 2;
      if (previous instanceof HTMLButtonElement) previous.disabled = this.scrollLeft <= 2;
      if (next instanceof HTMLButtonElement) next.disabled = this.scrollLeft >= maxScroll - 2;
    };

    arrows.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-recently-viewed-step]') : null;
      if (!(button instanceof HTMLButtonElement)) return;
      const card = this.firstElementChild;
      const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : this.clientWidth;
      this.scrollBy({ left: Number(button.dataset.recentlyViewedStep) * step, behavior: 'smooth' });
    });

    this.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /**
   * Lets mouse users drag the row sideways, like the other product sliders (touch and trackpads
   * scroll it natively). A drag does not open the product it ends on.
   */
  #enableMouseDrag() {
    if (this.#dragReady) return;
    this.#dragReady = true;

    let dragged = false;

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const startX = event.clientX;
      const startScroll = this.scrollLeft;
      dragged = false;

      /** @param {PointerEvent} moveEvent */
      const onMove = (moveEvent) => {
        const distance = moveEvent.clientX - startX;
        if (!dragged && Math.abs(distance) < 6) return;
        dragged = true;
        this.style.scrollSnapType = 'none';
        this.scrollLeft = startScroll - distance;
      };
      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        this.style.scrollSnapType = '';
      };
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    });

    this.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      },
      true
    );

    // Stop the browser's own image and link dragging from taking over.
    this.addEventListener('dragstart', (event) => event.preventDefault());
  }
}

if (!customElements.get('recently-viewed-products-list')) {
  customElements.define('recently-viewed-products-list', RecentlyViewedProductsList);
}
